import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { motion, AnimatePresence } from 'framer-motion';
import DOMPurify from 'dompurify';
import {
  X, Send, Plus, History, ArrowLeft, Check, XCircle,
  Pencil, Loader2, AlertTriangle, CheckCircle2, MessageCircle
} from 'lucide-react';
import api from '../../services/api';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';

// Minimal markdown → HTML for assistant bubbles: bold, italics, inline code,
// links, bullet lines and pipe tables. Escaped first, sanitized after — no
// raw model HTML ever reaches the DOM.
function mdToHtml(text) {
  let h = String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  h = h.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  h = h.replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>');
  h = h.replace(/`([^`\n]+)`/g, '<code>$1</code>');
  h = h.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, '<a href="$2">$1</a>');
  h = h.replace(/^#{1,4}\s+(.*)$/gm, '<strong>$1</strong>');
  h = h.replace(/^\s*[-•]\s+/gm, '• ');

  // Pipe tables → real <table>, wrapped for horizontal scroll in the
  // narrow panel. Header row must be followed by a |---|---| separator.
  const isRow = (l) => /^\s*\|.*\|\s*$/.test(l);
  const isSep = (l) => /^\s*\|[\s\-:|]+\|\s*$/.test(l);
  const cells = (l) => l.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
  const lines = h.split('\n');
  const out = [];
  for (let i = 0; i < lines.length; i++) {
    if (isRow(lines[i]) && i + 1 < lines.length && isSep(lines[i + 1])) {
      let j = i + 2;
      const rows = [];
      while (j < lines.length && isRow(lines[j]) && !isSep(lines[j])) { rows.push(lines[j]); j++; }
      let tbl = '<div class="tbl-wrap"><table><thead><tr>'
        + cells(lines[i]).map((c) => `<th>${c}</th>`).join('')
        + '</tr></thead><tbody>';
      for (const r of rows) tbl += '<tr>' + cells(r).map((c) => `<td>${c}</td>`).join('') + '</tr>';
      tbl += '</tbody></table></div>';
      out.push(tbl);
      i = j - 1;
    } else {
      out.push(lines[i]);
    }
  }
  h = out.join('\n')
    .replace(/\n(<div class="tbl-wrap">)/g, '$1')
    .replace(/(<\/table><\/div>)\n/g, '$1')
    .replace(/\n/g, '<br/>');

  return DOMPurify.sanitize(h, {
    ALLOWED_TAGS: ['strong', 'em', 'code', 'a', 'br', 'div', 'table', 'thead', 'tbody', 'tr', 'th', 'td'],
    ALLOWED_ATTR: ['href', 'class']
  });
}

const PARAM_LABELS = {
  leadId: 'Lead ID', stageId: 'Stage ID', stageName: 'Stage', taskId: 'Task ID',
  firstName: 'First name', lastName: 'Last name', estimatedValue: 'Est. value',
  dueDate: 'Due date', assignedTo: 'Assigned to'
};
const LONG_FIELDS = ['message', 'content', 'description'];
const HIDDEN_PARAMS = ['summary'];

function labelFor(key) {
  return PARAM_LABELS[key] || key.charAt(0).toUpperCase() + key.slice(1);
}

// ── Action card ──────────────────────────────────────────────────────

// Tool params are shown verbatim, except ISO timestamps, which read badly
// ("2026-09-20T10:00:00Z"). Render those in the viewer's locale instead;
// the edit form still holds the raw string so what gets executed is unchanged.
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?$/;
function displayValue(v) {
  if (typeof v === 'string' && ISO_DATE_RE.test(v)) {
    const d = new Date(v);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    }
  }
  return String(v);
}

function ActionCard({ msg, onResolved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const queryClient = useQueryClient();
  const action = msg.action || {};
  const params = action.params || {};
  const visibleKeys = Object.keys(params).filter((k) => !HIDDEN_PARAMS.includes(k));

  const startEdit = () => {
    const d = {};
    visibleKeys.forEach((k) => { d[k] = params[k] == null ? '' : String(params[k]); });
    setDraft(d);
    setEditing(true);
  };

  const resolve = async (verb) => {
    setBusy(true);
    try {
      const body = {};
      if (verb === 'approve' && editing) {
        body.params = {};
        visibleKeys.forEach((k) => {
          const orig = params[k];
          body.params[k] = typeof orig === 'number' ? Number(draft[k]) || 0 : draft[k];
        });
      }
      const { data } = await api.post(`/assistant/actions/${msg._id}/${verb}`, body);
      onResolved(data.action, data.followUp || null);
      if (verb === 'approve') {
        // The action mutated CRM data — refetch whatever the user is looking at.
        queryClient.invalidateQueries();
      }
    } catch (err) {
      const failed = {
        ...msg,
        action: { ...action, status: 'failed', error: err.response?.data?.error || err.message }
      };
      onResolved(failed, null);
    } finally {
      setBusy(false);
      setEditing(false);
    }
  };

  const status = action.status;
  const statusChip = {
    pending: null,
    executed: (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
        <CheckCircle2 size={12} /> Executed
      </span>
    ),
    rejected: (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 bg-gray-100 border border-gray-200 rounded-full px-2 py-0.5">
        <XCircle size={12} /> Dismissed
      </span>
    ),
    failed: (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">
        <AlertTriangle size={12} /> Failed
      </span>
    )
  }[status];

  return (
    <div className={`rounded-xl border p-3 text-sm shadow-sm ${
      status === 'pending' ? 'border-indigo-200 bg-indigo-50/60'
        : status === 'executed' ? 'border-green-200 bg-green-50/40'
          : status === 'failed' ? 'border-red-200 bg-red-50/40'
            : 'border-gray-200 bg-gray-50 opacity-80'
    }`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="font-semibold text-gray-800 leading-snug">{action.summary || action.tool}</div>
        {statusChip}
      </div>

      <div className="space-y-1.5 mb-2">
        {visibleKeys.map((k) => (
          <div key={k}>
            <div className="text-[11px] uppercase tracking-wide text-gray-400">{labelFor(k)}</div>
            {editing ? (
              LONG_FIELDS.includes(k) ? (
                <textarea
                  className="w-full border border-gray-300 rounded-lg p-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-indigo-400"
                  rows={4}
                  value={draft[k]}
                  onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
                />
              ) : (
                <input
                  className="w-full border border-gray-300 rounded-lg p-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-indigo-400"
                  value={draft[k]}
                  onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
                />
              )
            ) : (
              <div className={`text-gray-700 break-words ${LONG_FIELDS.includes(k) ? 'whitespace-pre-wrap bg-white/70 border border-gray-100 rounded-lg p-2' : ''}`}>
                {displayValue(params[k])}
              </div>
            )}
          </div>
        ))}
      </div>

      {status === 'failed' && action.error && (
        <div className="text-xs text-red-600 mb-2">{action.error}</div>
      )}

      {status === 'pending' && (
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => resolve('approve')}
            disabled={busy}
            className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg px-3 py-1.5"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            {editing ? 'Approve edited' : 'Approve'}
          </button>
          {!editing && (
            <button
              onClick={startEdit}
              disabled={busy}
              className="inline-flex items-center gap-1.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-lg px-3 py-1.5"
            >
              <Pencil size={14} /> Edit
            </button>
          )}
          <button
            onClick={() => resolve('reject')}
            disabled={busy}
            className="inline-flex items-center gap-1.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-500 text-sm font-medium rounded-lg px-3 py-1.5"
          >
            <XCircle size={14} /> Reject
          </button>
        </div>
      )}
    </div>
  );
}

// ── Panel ────────────────────────────────────────────────────────────

const INTRO_SEEN_KEY = 'rhino_intro_seen';
// The Gain Rhino shield mark from client/public — same asset as the PWA icon.
const RHINO_LOGO = '/web-app-manifest-512x512.png';

const AssistantPanel = () => {
  const [open, setOpen] = useState(false);
  const [showIntro, setShowIntro] = useState(() => !localStorage.getItem(INTRO_SEEN_KEY));
  const [showHistory, setShowHistory] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [statusText, setStatusText] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const bottomRef = useRef(null);
  const abortRef = useRef(null);
  const fabRef = useRef(null);
  // Pixels the launcher is lifted to clear a focused text field (see below).
  const [dodge, setDodge] = useState(0);

  // Collision dodge: the launcher floats over page content, so on any page
  // with interactive UI in the bottom-right corner (first hit: the Inbox
  // composer's Send button + toolbar) it blocks clicks. A focus-only
  // version missed exactly that case — buttons never take text focus — so
  // the launcher now probes what sits under its RESTING footprint and, if
  // any interactive element is there, lifts just above the highest one.
  // Probes run on route change (plus delayed re-checks for async content),
  // resize, focus changes, and pointer approach — deliberately NOT on
  // scroll, so content scrolling underneath never makes the button jump.
  const dodgeRef = useRef(0);
  useEffect(() => { dodgeRef.current = dodge; }, [dodge]);

  const computeDodge = useCallback(() => {
    const fab = fabRef.current;
    if (!fab) return;
    const INTERACTIVE = 'button, a[href], input, textarea, select, [role="button"], [contenteditable="true"]';
    const r = fab.getBoundingClientRect();
    // Rect the launcher would occupy at rest (transform y = -dodge).
    const rest = {
      left: r.left, right: r.right,
      top: r.top + dodgeRef.current, bottom: r.bottom + dodgeRef.current
    };
    const xs = [rest.left + 6, (rest.left + rest.right) / 2, rest.right - 6];
    const ys = [rest.top + 6, (rest.top + rest.bottom) / 2, rest.bottom - 6];
    let minTop = null;
    for (const x of xs) {
      for (const y of ys) {
        for (const el of document.elementsFromPoint(x, y)) {
          if (fab.contains(el)) continue;
          const hit = el.closest?.(INTERACTIVE);
          if (!hit || fab.contains(hit)) continue;
          const t = hit.getBoundingClientRect().top;
          if (minTop === null || t < minTop) minTop = t;
          break; // topmost interactive element at this point is enough
        }
      }
    }
    if (minTop === null) {
      setDodge(0);
    } else {
      setDodge(Math.min(Math.max(rest.bottom - minTop + 12, 0), window.innerHeight * 0.6));
    }
  }, []);

  useEffect(() => {
    // Async content (react-query fetches) mounts after the route settles —
    // re-probe a couple of times so late composers are still detected.
    const timers = [50, 400, 1500].map((ms) => setTimeout(computeDodge, ms));
    return () => timers.forEach(clearTimeout);
  }, [location.pathname, open, computeDodge]);

  useEffect(() => {
    let resizeTimer = null;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(computeDodge, 200);
    };
    const onFocusChange = () => setTimeout(computeDodge, 50);
    window.addEventListener('resize', onResize);
    document.addEventListener('focusin', onFocusChange);
    document.addEventListener('focusout', onFocusChange);
    return () => {
      window.removeEventListener('resize', onResize);
      document.removeEventListener('focusin', onFocusChange);
      document.removeEventListener('focusout', onFocusChange);
      clearTimeout(resizeTimer);
    };
  }, [computeDodge]);

  // Internal links in assistant replies route in-app; external open a tab.
  const onBubbleClick = useCallback((e) => {
    const a = e.target.closest('a');
    if (!a) return;
    const href = a.getAttribute('href') || '';
    e.preventDefault();
    if (href.startsWith('/')) navigate(href);
    else if (/^https?:\/\//.test(href)) window.open(href, '_blank', 'noopener');
  }, [navigate]);

  const { data: conversations } = useQuery(
    ['assistant-conversations'],
    () => api.get('/assistant/conversations').then((r) => r.data),
    { enabled: open }
  );

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, statusText, open]);

  const openConversation = async (id) => {
    try {
      const { data } = await api.get(`/assistant/conversations/${id}/messages`);
      setConversationId(id);
      setMessages(data.messages || []);
      setShowHistory(false);
    } catch (_) { /* toast handled by api interceptor */ }
  };

  const newChat = () => {
    setConversationId(null);
    setMessages([]);
    setShowHistory(false);
  };

  const dismissIntro = () => {
    localStorage.setItem(INTRO_SEEN_KEY, '1');
    setShowIntro(false);
  };

  const openPanel = () => {
    dismissIntro();
    setOpen(true);
  };

  const buildContext = useCallback(() => {
    const ctx = { page: location.pathname };
    const m = location.pathname.match(/\/leads\/([0-9a-fA-F]{24})/);
    if (m) ctx.leadId = m[1];
    return ctx;
  }, [location.pathname]);

  const handleEvent = useCallback((event, data) => {
    switch (event) {
      case 'conversation':
        setConversationId(data.conversationId);
        queryClient.invalidateQueries(['assistant-conversations']);
        break;
      case 'user_message':
        // Replace the optimistic bubble with the persisted doc.
        setMessages((prev) => [...prev.filter((x) => !x._optimistic), data]);
        break;
      case 'assistant_message':
      case 'action':
        setMessages((prev) => [...prev, data]);
        setStatusText(null);
        break;
      case 'status':
        setStatusText(data.state === 'tool' ? `Running ${data.tool}…` : 'Rhino is thinking…');
        break;
      case 'navigate':
        if (data.path && data.path.startsWith('/')) navigate(data.path);
        break;
      case 'error':
        setMessages((prev) => [...prev, {
          _id: `err-${prev.length}`, role: 'assistant', type: 'text',
          content: `⚠️ ${data.error || 'Assistant is temporarily unavailable.'}`
        }]);
        setStatusText(null);
        break;
      default:
        break;
    }
  }, [queryClient, navigate]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setSending(true);
    setStatusText('Rhino is thinking…');
    setMessages((prev) => [...prev, {
      _id: `opt-${Date.now()}`, _optimistic: true, role: 'user', type: 'text', content: text
    }]);

    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const resp = await fetch(`${API_BASE_URL}/assistant/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ conversationId, message: text, context: buildContext() }),
        signal: controller.signal
      });
      if (!resp.ok || !resp.body) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || `Request failed (${resp.status})`);
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split('\n\n');
        buffer = frames.pop();
        for (const frame of frames) {
          let event = 'message';
          let dataStr = '';
          for (const line of frame.split('\n')) {
            if (line.startsWith('event: ')) event = line.slice(7).trim();
            else if (line.startsWith('data: ')) dataStr += line.slice(6);
          }
          if (!dataStr) continue;
          try { handleEvent(event, JSON.parse(dataStr)); } catch (_) { /* skip bad frame */ }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        handleEvent('error', { error: err.message });
      }
    } finally {
      setSending(false);
      setStatusText(null);
      abortRef.current = null;
    }
  };

  const onActionResolved = (updatedAction, followUp) => {
    setMessages((prev) => {
      const next = prev.map((m) => (m._id === updatedAction._id ? updatedAction : m));
      return followUp ? [...next, followUp] : next;
    });
  };

  return (
    <>
      {/* Floating launcher */}
      <AnimatePresence>
        {!open && (
          <motion.div
            key="assistant-fab"
            ref={fabRef}
            initial={{ scale: 0 }}
            animate={{ scale: 1, y: -dodge }}
            exit={{ scale: 0 }}
            onPointerEnter={computeDodge}
            className="fixed bottom-5 right-5 z-40"
          >
            {/* First-run callout — until the panel is opened once */}
            {showIntro && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="absolute bottom-16 right-0 w-64 bg-white rounded-2xl shadow-xl border border-indigo-100 p-3.5"
              >
                <button
                  onClick={dismissIntro}
                  className="absolute top-2 right-2 p-1 text-gray-300 hover:text-gray-500"
                  title="Dismiss"
                >
                  <X size={14} />
                </button>
                <img src={RHINO_LOGO} alt="" className="w-8 h-8 mb-1.5" />
                <div className="font-semibold text-gray-800 text-sm mb-1">Meet Rhino</div>
                <div className="text-xs text-gray-500 leading-relaxed">
                  Your AI assistant. Search leads, create tasks, draft and send
                  emails — right from chat. You approve every action before it runs.
                </div>
                <div className="absolute -bottom-1.5 right-6 w-3 h-3 bg-white border-b border-r border-indigo-100 rotate-45" />
              </motion.div>
            )}
            <button
              onClick={openPanel}
              className="relative h-12 w-12 md:w-auto md:px-3.5 rounded-full bg-white hover:bg-orange-50 border-2 border-orange-500 shadow-lg flex items-center justify-center md:gap-1.5"
              title="Rhino — AI Assistant"
            >
              {showIntro && (
                <span className="absolute inset-0 rounded-full bg-orange-400 opacity-50 animate-ping" />
              )}
              <img src={RHINO_LOGO} alt="Rhino" className="relative w-9 h-9 md:w-8 md:h-8" />
              <span className="relative hidden md:inline font-semibold text-sm text-gray-700 pr-1">Ask Rhino</span>
              {/* Mobile-only chat affordance badge */}
              <span className="md:hidden absolute -bottom-1 -right-1 bg-orange-500 rounded-full p-[5px] border-2 border-white">
                <MessageCircle size={12} className="text-white" fill="currentColor" />
              </span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="assistant-panel"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.18 }}
            className="fixed z-50 inset-0 md:inset-auto md:right-5 md:bottom-5 md:w-[420px] md:h-[640px] md:max-h-[calc(100vh-40px)] bg-white md:rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-white">
              <div className="flex items-center gap-2.5 min-w-0">
                {showHistory ? (
                  <button onClick={() => setShowHistory(false)} className="p-1 text-gray-500 hover:bg-gray-100 rounded-lg">
                    <ArrowLeft size={18} />
                  </button>
                ) : (
                  <img src={RHINO_LOGO} alt="" className="w-8 h-8" />
                )}
                <div className="min-w-0">
                  <div className="font-semibold text-gray-800 truncate leading-tight">
                    {showHistory ? 'Conversations' : 'Rhino'}
                  </div>
                  {!showHistory && (
                    <div className="text-[11px] text-gray-400 leading-tight">AI Assistant</div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 text-gray-500">
                <button onClick={newChat} className="p-1.5 hover:bg-gray-100 rounded-lg" title="New chat">
                  <Plus size={18} />
                </button>
                <button onClick={() => setShowHistory((v) => !v)} className="p-1.5 hover:bg-gray-100 rounded-lg" title="History">
                  <History size={18} />
                </button>
                <button onClick={() => setOpen(false)} className="p-1.5 hover:bg-gray-100 rounded-lg" title="Close">
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Body */}
            {showHistory ? (
              <div className="flex-1 overflow-y-auto p-2">
                {(conversations || []).length === 0 && (
                  <div className="text-center text-gray-400 text-sm mt-10">No conversations yet</div>
                )}
                {(conversations || []).map((c) => (
                  <button
                    key={c._id}
                    onClick={() => openConversation(c._id)}
                    className={`w-full text-left px-3 py-2.5 rounded-xl hover:bg-gray-50 border-b border-gray-50 ${c._id === conversationId ? 'bg-indigo-50' : ''}`}
                  >
                    <div className="text-sm font-medium text-gray-800 truncate">{c.title}</div>
                    <div className="text-xs text-gray-400">{new Date(c.lastMessageAt).toLocaleString()}</div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto px-3 py-4 space-y-3 bg-gray-50/50">
                {messages.length === 0 && (
                  <div className="text-center mt-12 px-6">
                    <img src={RHINO_LOGO} alt="" className="w-14 h-14 mx-auto mb-3" />
                    <div className="font-medium text-gray-700 mb-1">Hi, I'm Rhino. How can I help?</div>
                    <div className="text-sm text-gray-400">
                      Try: "Find leads named John", "Create a follow-up task for this lead",
                      "How many deals did we win this month?"
                    </div>
                  </div>
                )}
                {messages.map((m) => (
                  m.type === 'action' ? (
                    <ActionCard key={m._id} msg={m} onResolved={onActionResolved} />
                  ) : (
                    <div key={m._id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      {m.role === 'user' ? (
                        <div className="max-w-[85%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap break-words bg-indigo-600 text-white rounded-br-md">
                          {m.content}
                        </div>
                      ) : (
                        <div
                          className="max-w-[92%] rounded-2xl px-3.5 py-2 text-sm break-words bg-white border border-gray-200 text-gray-800 rounded-bl-md shadow-sm assistant-bubble [&_a]:text-indigo-600 [&_a]:underline [&_a]:font-medium [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:rounded [&_code]:text-[13px] [&_.tbl-wrap]:overflow-x-auto [&_.tbl-wrap]:my-2 [&_.tbl-wrap]:rounded-lg [&_.tbl-wrap]:border [&_.tbl-wrap]:border-gray-200 [&_table]:w-full [&_table]:text-xs [&_table]:border-collapse [&_th]:text-left [&_th]:font-semibold [&_th]:bg-gray-50 [&_th]:px-2 [&_th]:py-1.5 [&_th]:whitespace-nowrap [&_th]:border-b [&_th]:border-gray-200 [&_td]:px-2 [&_td]:py-1.5 [&_td]:whitespace-nowrap [&_td]:border-b [&_td]:border-gray-100 [&_tbody_tr:last-child_td]:border-b-0"
                          onClick={onBubbleClick}
                          dangerouslySetInnerHTML={{ __html: mdToHtml(m.content) }}
                        />
                      )}
                    </div>
                  )
                ))}
                {statusText && (
                  <div className="flex items-center gap-2 text-xs text-gray-400 pl-1">
                    <Loader2 size={13} className="animate-spin" /> {statusText}
                  </div>
                )}
                <div ref={bottomRef} />
              </div>
            )}

            {/* Composer */}
            {!showHistory && (
              <div className="border-t border-gray-100 p-3 bg-white">
                <div className="flex items-end gap-2">
                  <textarea
                    rows={1}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    placeholder="Ask Rhino anything…"
                    className="flex-1 resize-none border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 max-h-32"
                  />
                  <button
                    onClick={sendMessage}
                    disabled={sending || !input.trim()}
                    className="p-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white"
                    title="Send"
                  >
                    {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AssistantPanel;
